import type { MarkdownViewModel, AddSoftwareResourceToDevicesViewModel, FileIdentifierViewModel } from "@vintecc/capture-api-types/v1";
import type { CreateResourceDto, EditResourceDto, EditVersionDto } from "@vintecc/capture-api-types/v2";

import { NewVersionPayload } from "@/modules/resources/types/resourceVersion";
import axios, { AxiosResponse } from "axios";
import software from "@/api/software.api";
import { apiDefaultParams } from "@/api/default.params";

const getResourcesApi = async () => (await import("@/api/resource.api")).default;

export const requests = {
  key: "resources",
  getAll: async () => {
    const response = await software.softwareResourcesOverview(
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
    );
    return response.data;
  },
  getAgenticResources: async () => {
    const resources = await getResourcesApi();
    const response = await resources.getResources(
      true,
      undefined,
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
    );
    return response.data;
  },
  getById: async (id: number) => {
    const response = await software.resourceDetail(id, apiDefaultParams.authorization, apiDefaultParams.authVersion, apiDefaultParams.companyView);
    return response.data;
  },
  getVersionById: async (versionId: number) => {
    const resources = await getResourcesApi();
    const response = await resources.getVersion(
      versionId,
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
    );
    return response.data;
  },
  delete: async (id: number) => {
    const response = await software.deleteSoftwareResource(
      id,
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
    );
    return response.data;
  },
  deleteVersion: async (versionId: number) => {
    const response = await software.deleteSoftwareVersion(
      versionId,
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
    );
    return response.data;
  },
  create: async (payload: CreateResourceDto) => {
    const resources = await getResourcesApi();
    const response = await resources.createResource(
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
      payload,
    );
    return response.data;
  },
  editResource: async ({ resourceId, payload }: { resourceId: number; payload: EditResourceDto }) => {
    const resources = await getResourcesApi();
    const response = await resources.updateResource(
      resourceId,
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
      payload,
    );
    return response.data;
  },
  editResourceMarkdown: async (payload: MarkdownViewModel) => {
    const response = await software.editSoftwareResourceMarkdown(
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
      payload,
    );
    return response.data;
  },
  linkEdgeGateways: async (payload: AddSoftwareResourceToDevicesViewModel) => {
    const response = await software.addSoftwareResourceToDevices(apiDefaultParams.authorization, apiDefaultParams.authVersion, payload);
    return response.data;
  },
  unlinkEdgeGateway: async (softwareResourceId: number, deviceId: number) => {
    const response = await software.deleteSoftwareResourceFromDevice(
      softwareResourceId,
      deviceId,
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
    );
    return response.data;
  },
  createVersion: async (resourceId: number, payload: NewVersionPayload) => {
    const resources = await getResourcesApi();
    const response = await resources.createVersion(
      resourceId,
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
      payload.version,
      payload.description,
      payload.changelogs,
      payload.KeyValueJson,
      payload.KeyValueIdentifier,
      payload.fileIdentifiers ? JSON.stringify(payload.fileIdentifiers) : undefined,
      payload.files,
      payload.recommended,
    );
    return response.data;
  },
  updateVersion: async (versionId: number, payload: EditVersionDto) => {
    const resources = await getResourcesApi();
    const response = await resources.editVersion(
      versionId,
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
      payload,
    );
    return response.data;
  },
  linkToFleets: async (resourceId: number, fleetIds: number[]) => {
    const resources = await getResourcesApi();
    const response = await resources.addSoftwareResourceToFleets(
      resourceId,
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
      fleetIds,
    );
    return response.data;
  },
  linkToProjects: async (resourceId: number, projectIds: number[]) => {
    const resources = await getResourcesApi();
    const response = await resources.addSoftwareResourceToProjects(
      resourceId,
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
      projectIds,
    );
    return response.data;
  },
  changeFileIdentifier: async (payload: FileIdentifierViewModel) => {
    const response = await software.changeFileIdentifier(apiDefaultParams.authorization, apiDefaultParams.authVersion, payload);
    return response.data;
  },
  getResourceTypes: async () => {
    const response = await software.getSoftwareResourceTypes(
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
    );
    return response.data;
  },
  getFileForDownload: async (id: number): Promise<{ blob: Blob; filename: string }> => {
    try {
      const response = await axios.get(`/v1/Software/DownloadSoftwareVersion`, {
        params: { id },
        headers: {
          Authorization: apiDefaultParams.authorization,
          "Auth-Version": apiDefaultParams.authVersion,
          "Company-View": apiDefaultParams.companyView,
        },
        responseType: "blob",
      });

      return {
        blob: response.data,
        filename: getFileNameFromRequestResponse(response),
      };
    } catch (error) {
      console.error("Failed to download file:", error);
      throw new Error("Failed to download file");
    }
  },
};

function getFileNameFromRequestResponse(response: AxiosResponse): string {
  const contentDisposition = response.headers["content-disposition"];
  const filenameMatch = contentDisposition?.match(/filename=([^;]+)/);
  const filenameStarMatch = contentDisposition?.match(/filename\*=UTF-8''(.+)/);

  // Use filename* if available, otherwise fallback to filename
  const filename = filenameStarMatch ? decodeURIComponent(filenameStarMatch[1]) : filenameMatch ? filenameMatch[1].trim() : "downloaded-file";

  return filename;
}
